import React, { useState, useEffect, useCallback } from 'react';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const MAX_CHARS = 1024;
const NAME_VAR_LENGTH = 30;
const TEMPLATES_LIMIT = 20;
const BUTTON_TEXT_MAX = 20;

const TemplateModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  selectedGenders, 
  selectedSources,
  mode = 'whatsapp' // 'whatsapp' or 'notification'
}) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [dynamicVariables, setDynamicVariables] = useState([]);
  const [variableValues, setVariableValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [baseMessageLength, setBaseMessageLength] = useState(0);
  const [platform, setPlatform] = useState('');
  const [showPlatformSelection, setShowPlatformSelection] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const [sending, setSending] = useState(false);
  // Media state
  const [mediaType, setMediaType] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaError, setMediaError] = useState('');
  // Buttons state
  const [buttons, setButtons] = useState({ quickReplies: [], actions: [] });
  const [buttonValues, setButtonValues] = useState({});
  const [buttonErrors, setButtonErrors] = useState({});
  // Notification specific state
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [validTillDate, setValidTillDate] = useState('');
  const [useButtons, setUseButtons] = useState(false);
  // Notification button text fields (similar to NotificationModal)
  const [button1Text, setButton1Text] = useState('');
  const [button2Text, setButton2Text] = useState('');
  const [button1Error, setButton1Error] = useState('');
  const [button2Error, setButton2Error] = useState('');
  // Pagination state for notification templates
  const [tplPage, setTplPage] = useState(1);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplError, setTplError] = useState('');
  const [hasMore, setHasMore] = useState(true);

  // Fetch templates with pagination for notification mode
  const fetchNotificationTemplates = useCallback(async (page = 1, reset = false) => {
    if (tplLoading) return;
    setTplLoading(true);
    setTplError('');
    try {
      const token = localStorage.getItem('superadmintoken');
      const qs = new URLSearchParams({ 
        page, 
        limit: TEMPLATES_LIMIT 
      });
      const response = await fetch(
        `${BASE_URL}/lenskart-admin/list-push-notification-templates?${qs}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Handle different response formats
      const items = Array.isArray(data)
        ? data
        : data?.templates ?? data?.data ?? [];

      setTemplates(prev => (reset ? items : [...prev, ...items]));
      setHasMore(items.length === TEMPLATES_LIMIT);
      setTplPage(page);
    } catch (err) {
      console.error('Template fetch failed:', err);
      setTplError('Failed to load templates. Tap to retry.');
    } finally {
      setTplLoading(false);
    }
  }, [BASE_URL, tplLoading]);

  // Fetch templates for WhatsApp mode (existing)
  const fetchWhatsAppTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('superadmintoken');
      const response = await fetch(`${BASE_URL}/lenskart-admin/list-templates`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await response.json();
      
      if (data.success) {
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Main fetch templates function
  const fetchTemplates = useCallback(async () => {
    if (mode === 'whatsapp') {
      await fetchWhatsAppTemplates();
    } else {
      await fetchNotificationTemplates(1, true);
    }
  }, [mode]);

  // Load more templates for notification mode (infinite scroll)
  const loadMoreTemplates = useCallback(() => {
    if (mode === 'notification' && hasMore && !tplLoading) {
      fetchNotificationTemplates(tplPage + 1, false);
    }
  }, [mode, hasMore, tplLoading, tplPage, fetchNotificationTemplates]);

  // Handle scroll in templates list for infinite scroll
  const handleTemplatesScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      loadMoreTemplates();
    }
  }, [loadMoreTemplates]);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      setShowPlatformSelection(false);
      setSelectedPlatform('');
      setMessageSent(false);
      setSending(false);
      // Reset media state
      setMediaType('');
      setMediaUrl('');
      setMediaError('');
      // Reset buttons state
      setButtons({ quickReplies: [], actions: [] });
      setButtonValues({});
      setButtonErrors({});
      // Reset notification state
      if (mode === 'notification') {
        setNotificationTitle('');
        setNotificationBody('');
        setValidTillDate('');
        setUseButtons(false);
        setButton1Text('');
        setButton2Text('');
        setButton1Error('');
        setButton2Error('');
      }
    } else {
      setSelectedTemplate(null);
      setTemplatePreview(null);
      setDynamicVariables([]);
      setVariableValues({});
      setCharCount(0);
      setBaseMessageLength(0);
      setPlatform('');
      setShowPlatformSelection(false);
      setSelectedPlatform('');
      setMessageSent(false);
      setSending(false);
      // Reset media state
      setMediaType('');
      setMediaUrl('');
      setMediaError('');
      // Reset buttons state
      setButtons({ quickReplies: [], actions: [] });
      setButtonValues({});
      setButtonErrors({});
      // Reset notification state
      setNotificationTitle('');
      setNotificationBody('');
      setValidTillDate('');
      setUseButtons(false);
      setButton1Text('');
      setButton2Text('');
      setButton1Error('');
      setButton2Error('');
      // Reset pagination state
      setTplPage(1);
      setHasMore(true);
      setTplError('');
    }
  }, [isOpen, mode]);

  // Calculate total character count whenever variable values change
  useEffect(() => {
    if (templatePreview && templatePreview.templateMessage) {
      calculateTotalChars();
    }
  }, [variableValues, templatePreview, dynamicVariables, buttonValues]);

  const calculateTotalChars = () => {
    if (!templatePreview?.templateMessage) {
      setCharCount(0);
      return;
    }

    // Start with the base message length
    let totalChars = baseMessageLength;

    // Add the length of all variable values with special handling for ${name}
    dynamicVariables.forEach(variable => {
      const value = variableValues[variable] || '';
      if (value === '${name}') {
        // ${name} takes 30 characters
        totalChars += NAME_VAR_LENGTH;
      } else {
        // Regular values take their actual length
        totalChars += value.length;
      }
    });

    setCharCount(totalChars);
  };

  // Calculate the length of base message without variables
  const calculateBaseMessageLength = (message, variables) => {
    if (!message) return 0;
    let baseMessage = message;
    variables.forEach((variable) => {
      const placeholder = `{{${variable}}}`;
      baseMessage = baseMessage.split(placeholder).join('');
    });
    return baseMessage.length;
  };

  // Extract dynamic variables from template message
  const extractDynamicVariables = (message) => {
    if (!message) return [];
    
    // Find all {{number}} patterns
    const matches = message.match(/\{\{(\d+)\}\}/g);
    if (!matches) return [];
    
    // Extract unique numbers and sort them
    const numbers = [...new Set(matches.map(m => parseInt(m.match(/\d+/)[0])))].sort((a, b) => a - b);
    
    // Convert numbers to string array (like ["1", "2", "3"])
    return numbers.map(num => String(num));
  };

  // Validate button text
  const validateButtonText = (v) => {
    if (!v.trim()) return 'Required.';
    if (v.length > BUTTON_TEXT_MAX) return `Max ${BUTTON_TEXT_MAX} characters.`;
    return '';
  };

  // Handle button text changes
  const handleButton1Change = (e) => {
    const v = e.target.value;
    if (v.length > BUTTON_TEXT_MAX + 5) return;
    setButton1Text(v);
    if (button1Error) setButton1Error(validateButtonText(v));
  };

  const handleButton2Change = (e) => {
    const v = e.target.value;
    if (v.length > BUTTON_TEXT_MAX + 5) return;
    setButton2Text(v);
    if (button2Error) setButton2Error(validateButtonText(v));
  };

  // Toggle useButtons
  const handleToggleUseButtons = () => {
    setUseButtons(prev => {
      const next = !prev;
      // Clear button state whenever buttons are turned off
      if (!next) {
        setButton1Error('');
        setButton2Error('');
        setButton1Text('');
        setButton2Text('');
      }
      return next;
    });
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setLoadingPreview(true);
    
    try {
      // Extract data directly from the template object
      const templateMessage = template.templateMessage || '';
      const templateTitle = template.template_title || '';
      const platformFromApi = template.platform || '';
      const buttonsFromApi = template.buttons || { quickReplies: [], actions: [] };
      
      // For notification mode, set title and body
      if (mode === 'notification') {
        setNotificationTitle(templateTitle);
        setNotificationBody(templateMessage);
        
        // Check if template has buttons from the response structure
        let hasButtons = false;
        let button1Value = '';
        let button2Value = '';
        
        if (buttonsFromApi.quickReplies && buttonsFromApi.quickReplies.length > 0) {
          hasButtons = true;
          // Get button 1 (index 0)
          const button1 = buttonsFromApi.quickReplies.find(b => b.index === 0);
          if (button1 && button1.payload) {
            button1Value = button1.payload;
          }
          
          // Get button 2 (index 1)
          const button2 = buttonsFromApi.quickReplies.find(b => b.index === 1);
          if (button2 && button2.payload) {
            button2Value = button2.payload;
          }
        }
        
        // Also check for direct useButtons field for backward compatibility
        if (template.useButtons === true) {
          hasButtons = true;
        }
        
        setUseButtons(hasButtons);
        setButton1Text(button1Value);
        setButton2Text(button2Value);
        setButton1Error('');
        setButton2Error('');
      }
      
      // Extract dynamic variables from the template message
      const extractedVariables = extractDynamicVariables(templateMessage);
      
      // For WhatsApp mode, we also need to handle dynamicVariables from API
      let variables = extractedVariables;
      if (mode === 'whatsapp' && template.dynamicVariables) {
        // If dynamicVariables is an array, use it
        if (Array.isArray(template.dynamicVariables)) {
          variables = template.dynamicVariables;
        } 
        // If dynamicVariables is an object with numeric keys, extract values
        else if (typeof template.dynamicVariables === 'object') {
          const values = Object.values(template.dynamicVariables);
          if (values.length > 0 && values.every(v => typeof v === 'string')) {
            variables = values;
          }
        }
      }
      
      // If no variables extracted, try to get from template.dynamicVariables
      if (variables.length === 0 && template.dynamicVariables) {
        if (Array.isArray(template.dynamicVariables)) {
          variables = template.dynamicVariables;
        } else if (typeof template.dynamicVariables === 'object') {
          const values = Object.values(template.dynamicVariables);
          if (values.length > 0 && values.every(v => typeof v === 'string')) {
            variables = values;
          }
        }
      }
      
      // For notification mode, always use extracted variables from message
      if (mode === 'notification') {
        variables = extractDynamicVariables(templateMessage);
      }
      
      setDynamicVariables(variables);
      
      // Initialize variable values
      const initialValues = {};
      variables.forEach(variable => {
        if (typeof variable === 'string') {
          initialValues[variable] = '';
        }
      });
      setVariableValues(initialValues);
      
      // Create template preview
      const previewData = {
        templateMessage: templateMessage,
      };
      setTemplatePreview(previewData);
      setPlatform(platformFromApi);
      
      // Set buttons for WhatsApp mode
      if (mode === 'whatsapp') {
        setButtons(buttonsFromApi);
        
        // Initialize button values
        const initialButtonValues = {};
        const initialButtonErrors = {};
        
        if (buttonsFromApi.quickReplies && Array.isArray(buttonsFromApi.quickReplies)) {
          buttonsFromApi.quickReplies.forEach(button => {
            if (button._id && button.payload) {
              initialButtonValues[button._id] = button.payload;
            }
          });
        }
        if (buttonsFromApi.actions && Array.isArray(buttonsFromApi.actions)) {
          buttonsFromApi.actions.forEach(button => {
            if (button._id && button.payload) {
              initialButtonValues[button._id] = button.payload;
            }
          });
        }
        setButtonValues(initialButtonValues);
        setButtonErrors(initialButtonErrors);
      }
      
      // Calculate base message length
      const baseLength = calculateBaseMessageLength(templateMessage, variables);
      setBaseMessageLength(baseLength);
      setCharCount(baseLength);
      
      setShowPlatformSelection(false);
      setSelectedPlatform('');
      setMessageSent(false);
      setSending(false);
      
    } catch (error) {
      console.error('Error processing template:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleVariableChange = (variableName, value) => {
    // Ensure value is always a string
    const stringValue = typeof value === 'string' ? value : String(value || '');
    
    const currentValue = variableValues[variableName] || '';
    
    let currentLength = 0;
    let newLength = 0;
    
    if (currentValue === '${name}') {
      currentLength = NAME_VAR_LENGTH;
    } else {
      currentLength = currentValue.length;
    }
    
    if (stringValue === '${name}') {
      newLength = NAME_VAR_LENGTH;
    } else {
      newLength = stringValue.length;
    }
    
    const lengthDiff = newLength - currentLength;
    
    if (charCount + lengthDiff > MAX_CHARS) {
      const remainingChars = MAX_CHARS - charCount + currentLength;
      if (remainingChars > 0 && stringValue !== '${name}') {
        value = stringValue.substring(0, remainingChars);
      } else {
        return;
      }
    }
    
    setVariableValues(prev => ({
      ...prev,
      [variableName]: stringValue
    }));
  };

  const handleButtonChange = (buttonId, value) => {
    // Always update the value, even if empty
    setButtonValues(prev => ({
      ...prev,
      [buttonId]: value
    }));

    // Validate quick reply button - should not be empty for sending
    if (buttons.quickReplies?.some(btn => btn._id === buttonId)) {
      if (!value.trim()) {
        setButtonErrors(prev => ({
          ...prev,
          [buttonId]: 'Button text cannot be empty'
        }));
      } else {
        setButtonErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[buttonId];
          return newErrors;
        });
      }
    }
  };

  const handleToggleVariable = (variableName) => {
    const currentValue = variableValues[variableName];
    const nameVar = '${name}';
    
    if (currentValue === nameVar) {
      setVariableValues(prev => ({
        ...prev,
        [variableName]: ''
      }));
    } else {
      const currentLength = currentValue?.length || 0;
      const nameVarLength = NAME_VAR_LENGTH;
      const lengthDiff = nameVarLength - currentLength;
      
      if (charCount + lengthDiff <= MAX_CHARS) {
        setVariableValues(prev => ({
          ...prev,
          [variableName]: nameVar
        }));
      } else {
        alert(`Cannot add variable. It would exceed the character limit.`);
      }
    }
  };

  const isVariableUsed = (variableName) => {
    return variableValues[variableName] === '${name}';
  };

  const allVariablesFilled = () => {
    return dynamicVariables.every(
      variable => variableValues[variable]?.trim() !== ''
    );
  };

  // Validate media URL
  const validateMediaUrl = (url) => {
    if (!url.trim()) return true; // Empty is valid (no media)
    
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Check if all quick reply buttons have text
  const allQuickReplyButtonsFilled = () => {
    if (!buttons.quickReplies || buttons.quickReplies.length === 0) return true;
    
    return buttons.quickReplies.every(button => {
      const buttonText = buttonValues[button._id] !== undefined ? buttonValues[button._id] : button.payload;
      return buttonText && buttonText.trim() !== '';
    });
  };

  const canSendMessage = () => {
    const mediaValid = !mediaType || (mediaType && mediaUrl.trim() && validateMediaUrl(mediaUrl));
    
    // For notification mode, check additional validation
    if (mode === 'notification') {
      // Check button validation if useButtons is true
      if (useButtons) {
        const b1Valid = button1Text.trim() !== '' && button1Text.length <= BUTTON_TEXT_MAX;
        const b2Valid = button2Text.trim() !== '' && button2Text.length <= BUTTON_TEXT_MAX;
        if (!b1Valid || !b2Valid) return false;
      }
      
      return selectedTemplate && 
             (dynamicVariables.length === 0 || allVariablesFilled()) &&
             charCount <= MAX_CHARS &&
             charCount > 0 &&
             !sending &&
             !messageSent &&
             validTillDate !== '';
    }
    
    return selectedTemplate && 
           (dynamicVariables.length === 0 || allVariablesFilled()) &&
           charCount <= MAX_CHARS &&
           charCount > 0 &&
           !sending &&
           !messageSent &&
           mediaValid;
  };

  const getRemainingChars = () => {
    return MAX_CHARS - charCount;
  };

  // Safe function to handle selectedGenders (convert to array if needed)
  const getSelectedGendersArray = () => {
    if (!selectedGenders) return [];
    if (Array.isArray(selectedGenders)) return selectedGenders;
    if (typeof selectedGenders === 'string') return [selectedGenders];
    return [];
  };

  // Safe function to handle selectedSources (convert to array if needed)
  const getSelectedSourcesArray = () => {
    if (!selectedSources) return [];
    if (Array.isArray(selectedSources)) return selectedSources;
    if (typeof selectedSources === 'string') return [selectedSources];
    return [];
  };

  const handleMediaUrlChange = (url) => {
    setMediaUrl(url);
    
    if (mediaType && url.trim()) {
      if (!validateMediaUrl(url)) {
        setMediaError('Please enter a valid URL');
      } else {
        setMediaError('');
      }
    } else if (mediaType && !url.trim()) {
      setMediaError('Media URL is required when media type is selected');
    } else {
      setMediaError('');
    }
  };

  // Get updated buttons with user modifications
  const getUpdatedButtons = () => {
    const updatedButtons = {
      quickReplies: buttons.quickReplies ? [...buttons.quickReplies] : [],
      actions: buttons.actions ? [...buttons.actions] : []
    };

    // Update quickReplies payloads
    if (updatedButtons.quickReplies) {
      updatedButtons.quickReplies = updatedButtons.quickReplies.map(button => ({
        ...button,
        payload: buttonValues[button._id] || button.payload
      }));
    }

    // Update actions payloads (only if they exist and are editable, but currently keeping them non-editable)
    if (updatedButtons.actions) {
      updatedButtons.actions = updatedButtons.actions.map(button => ({
        ...button,
        // For actions, we don't update the payload as they are non-editable
        payload: button.payload
      }));
    }

    return updatedButtons;
  };

  // Function to replace variables in the message
  const replaceVariablesInMessage = (message, variables, values) => {
    if (!message) return '';
    let completeMessage = message;
    variables.forEach(variable => {
      const value = (values[variable] ?? '').toString().trim();
      const placeholder = `{{${variable}}}`;
      completeMessage = completeMessage.split(placeholder).join(value);
    });
    return completeMessage;
  };

  const handleConfirm = async () => {
    if (selectedTemplate) {
      if (dynamicVariables.length > 0 && !allVariablesFilled()) {
        alert('Please fill in all required variables');
        return;
      }

      if (mode === 'whatsapp') {
        // Existing WhatsApp logic
        if (charCount > MAX_CHARS) {
          alert(`Message exceeds character limit. Please reduce content by ${charCount - MAX_CHARS} characters.`);
          return;
        }

        // Validate media
        if (mediaType && !mediaUrl.trim()) {
          setMediaError('Media URL is required when media type is selected');
          return;
        }

        if (mediaType && mediaUrl.trim() && !validateMediaUrl(mediaUrl)) {
          setMediaError('Please enter a valid URL');
          return;
        }

        const platformLower = platform.toLowerCase();
        if (platformLower === 'all' || platformLower === 'both') {
          setShowPlatformSelection(true);
          return;
        }

        setSending(true);

        try {
          const gendersArray = getSelectedGendersArray();
          const sourcesArray = getSelectedSourcesArray();

          const mediaData = mediaType && mediaUrl.trim() ? {
            type: mediaType,
            url: mediaUrl.trim()
          } : null;

          const buttonsData = getUpdatedButtons();

          const result = await onConfirm(selectedTemplate, variableValues, gendersArray, sourcesArray, platform, mediaData, buttonsData);
          
          if (result && result.success) {
            setMessageSent(true);
            setTimeout(() => {
              onClose();
            }, 1000);
          } else {
            console.error('Failed to send message:', result?.error);
          }
        } catch (error) {
          console.error('Error sending message:', error);
        } finally {
          setSending(false);
        }
      } else {
        // Notification mode logic
        if (!validTillDate) {
          alert('Please select a valid date');
          return;
        }

        // Validate button texts if useButtons is true
        if (useButtons) {
          const b1Error = validateButtonText(button1Text);
          const b2Error = validateButtonText(button2Text);
          setButton1Error(b1Error);
          setButton2Error(b2Error);
          if (b1Error || b2Error) {
            setSending(false);
            return;
          }
        }

        setSending(true);
        try {
          const gendersArray = getSelectedGendersArray();
          const sourcesArray = getSelectedSourcesArray();

          // Get the template message from the selected template
          const templateMessage = selectedTemplate.templateMessage || '';
          
          // Replace all variables with their values
          const completeMessage = replaceVariablesInMessage(templateMessage, dynamicVariables, variableValues);
          
          const title = notificationTitle;
          const body = completeMessage; 

          // Build notification payload with the replaced message
          const payload = {
            filterSource: 'send_notification',
            hasAppToken: true,
            notificationBody: body, 
            validTillDate: validTillDate,
            genders: gendersArray,
            sources: sourcesArray,
            templateId: selectedTemplate._id,
            templateName: selectedTemplate.templateName,
            useButtons: useButtons,
            dynamicVariables: JSON.stringify(dynamicVariables.map(v => variableValues[v] || '')),
          };

          if (title && title.trim()) {
  payload.notificationTitle = title.trim();
}

          // Add button1 and button2 if useButtons is true
          if (useButtons) {
            payload.button1 = button1Text.trim();
            payload.button2 = button2Text.trim();
          }

          console.log('Final Payload being sent:', payload);

          const result = await onConfirm(payload);
          
          if (result && result.success) {
            setMessageSent(true);
            setTimeout(() => {
              onClose();
            }, 1000);
          } else {
            console.error('Failed to send notification:', result?.error);
          }
        } catch (error) {
          console.error('Error sending notification:', error);
        } finally {
          setSending(false);
        }
      }
    }
  };

  const handlePlatformSelect = async (selectedPlatform) => {
    setSelectedPlatform(selectedPlatform);
    setSending(true);
    
    try {
      const gendersArray = getSelectedGendersArray();
      const sourcesArray = getSelectedSourcesArray();

      // Prepare media data for backend
      const mediaData = mediaType && mediaUrl.trim() ? {
        type: mediaType,
        url: mediaUrl.trim()
      } : null;

      // Prepare buttons data for backend
      const buttonsData = getUpdatedButtons();

      const result = await onConfirm(selectedTemplate, variableValues, gendersArray, sourcesArray, selectedPlatform, mediaData, buttonsData);
      
      if (result && result.success) {
        setMessageSent(true);
        setTimeout(() => {
          setShowPlatformSelection(false);
          onClose();
        }, 1000);
      } else {
        console.error('Failed to send message:', result?.error);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const renderMessageWithVariables = (message) => {
    if (!message) return null;
    
    // Create a pattern that matches {{any_word}} format
    const parts = message.split(/(\{\{\w+\}\})/g);
    
    return parts.map((part, index) => {
      const match = part.match(/\{\{(\w+)\}\}/);
      if (match) {
        const variableName = match[1];
        const value = variableValues[variableName] || '';
        return (
          <span 
            key={index} 
            className="inline-block px-2 py-0.5 mx-0.5 bg-indigo-100 text-indigo-700 rounded-md text-xs font-semibold border border-indigo-200 break-words max-w-full"
          >
            {value && value.trim() ? value : `{{${variableName}}}`}
          </span>
        );
      }
      return (
        <span key={index} className="break-words whitespace-pre-wrap inline">
          {part}
        </span>
      );
    });
  };

  // Function to format platform name for display
  const formatPlatformName = (platform) => {
    if (!platform) return '';
    if (platform.toLowerCase() === 'mtalkz') return 'Mtalkz';
    if (platform.toLowerCase() === 'factrika') return 'Factrika';
    return platform;
  };

  // Function to check if platform should be displayed
  const shouldDisplayPlatform = (platform) => {
    if (!platform) return false;
    const platformLower = platform.toLowerCase();
    return platformLower !== 'all' && platformLower !== 'both';
  };

  // Check if template has any buttons
  const hasButtons = () => {
    return (buttons.quickReplies && buttons.quickReplies.length > 0) || 
           (buttons.actions && buttons.actions.length > 0);
  };

  // Get button display text
  const getButtonDisplayText = (button) => {
    return buttonValues[button._id] || button.payload || 'Button';
  };

  // Handle URL click for action buttons
  const handleUrlClick = (url, event) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  // Platform Selection Modal
  if (showPlatformSelection) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 text-center">
            Select Platform to Send Message
          </h2>
          <div className="space-y-3">
            <button
              onClick={() => handlePlatformSelect('Factrika')}
              className="w-full bg-blue-500 text-white px-4 py-3 rounded-md hover:bg-blue-600 transition-colors font-medium"
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Send using Factrika'}
            </button>
            <button
              onClick={() => handlePlatformSelect('Mtalkz')}
              className="w-full bg-green-500 text-white px-4 py-3 rounded-md hover:bg-green-600 transition-colors font-medium"
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Send using Mtalkz'}
            </button>
            <button
              onClick={() => setShowPlatformSelection(false)}
              className="w-full bg-gray-500 text-white px-4 py-3 rounded-md hover:bg-gray-600 transition-colors font-medium"
              disabled={sending}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  const modalTitle = mode === 'whatsapp' ? 'Select Whatsapp Template' : 'Select Notification Template';
  const sendButtonText = mode === 'whatsapp' ? 'Send Message' : 'Send Notification';
  const successMessage = mode === 'whatsapp' ? 'Message Sent' : 'Notification Sent';
  const headerColor = mode === 'whatsapp' ? '#E2E1FD' : '#E2E1FD';
  const accentColor = mode === 'whatsapp' ? 'indigo' : 'indigo';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] flex flex-col shadow-lg">
        <div className="px-5 py-3 border-b flex justify-center items-center relative" style={{ backgroundColor: headerColor }}>
          <h2 className="text-lg font-semibold text-gray-800">{modalTitle}</h2>
          <button
            onClick={onClose}
            className="absolute right-4 text-gray-700 hover:text-gray-900 text-2xl font-light transition-colors"
            disabled={sending}
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
          {/* Templates List */}
          <div className="w-full lg:w-1/3 border-b lg:border-r lg:border-b-0 overflow-y-auto p-3 bg-white">
            <h3 className="font-semibold mb-2 text-gray-800 text-sm flex items-center">
              <span className={`w-1 h-4 bg-${accentColor}-500 mr-2 rounded`}></span>
              Available Templates
            </h3>
            {mode === 'whatsapp' ? (
              // WhatsApp templates loading
              loading ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
                  <p className="mt-2">Loading templates...</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  No templates available
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map((template) => (
                    <div
                      key={template._id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`p-2.5 border rounded-md cursor-pointer transition-all ${
                        selectedTemplate?._id === template._id
                          ? `bg-${accentColor}-50 border-${accentColor}-500 shadow-sm`
                          : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-indigo-300'
                      } ${sending ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{ pointerEvents: sending ? 'none' : 'auto' }}
                    >
                      <h4 className="font-medium text-sm text-gray-800 break-words">{template.templateName}</h4>
                      {template.description && (
                        <p className="text-xs text-gray-500 mt-1 break-words">{template.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : (
              // Notification templates with pagination and infinite scroll
              <div 
                className="space-y-2 max-h-[400px] overflow-y-auto"
                onScroll={handleTemplatesScroll}
              >
                {tplError ? (
                  <div className="text-center py-8">
                    <p className="text-red-500 text-sm">{tplError}</p>
                    <button
                      onClick={() => fetchNotificationTemplates(1, true)}
                      className="mt-2 text-indigo-500 hover:text-indigo-700 text-sm font-medium"
                    >
                      Retry
                    </button>
                  </div>
                ) : templates.length === 0 && !tplLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    No notification templates available
                  </div>
                ) : (
                  <>
                    {templates.map((template) => (
                      <div
                        key={template._id || template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className={`p-2.5 border rounded-md cursor-pointer transition-all ${
                          selectedTemplate?._id === template._id || selectedTemplate?.id === template.id
                            ? `bg-${accentColor}-50 border-${accentColor}-500 shadow-sm`
                            : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-indigo-300'
                        } ${sending ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{ pointerEvents: sending ? 'none' : 'auto' }}
                      >
                        <h4 className="font-medium text-sm text-gray-800 break-words">
                          {template.templateName || template.name || 'Unnamed Template'}
                        </h4>
                        {template.description && (
                          <p className="text-xs text-gray-500 mt-1 break-words">{template.description}</p>
                        )}
                      </div>
                    ))}
                    {tplLoading && (
                      <div className="text-center py-4">
                        <div className="animate-spin h-6 w-6 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
                        <p className="text-xs text-gray-500 mt-1">Loading more...</p>
                      </div>
                    )}
                    {!hasMore && templates.length > 0 && (
                      <div className="text-center py-4 text-xs text-gray-400">
                        No more templates to load
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Template Preview and Variables */}
          <div className="w-full lg:w-2/3 overflow-y-auto p-4">
            {!selectedTemplate ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <svg className="w-20 h-20 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                <p className="text-lg">Select a template to view preview</p>
              </div>
            ) : loadingPreview ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <div className="animate-spin h-12 w-12 border-4 border-indigo-500 border-t-transparent rounded-full mb-4"></div>
                <p>Loading preview...</p>
              </div>
            ) : templatePreview ? (
              <div className="space-y-4 overflow-y-auto">
                {/* Character Count Display */}
                <div className={`px-4 py-2 rounded-md border text-sm font-medium ${
                  charCount > MAX_CHARS 
                    ? 'bg-red-50 border-red-200 text-red-700' 
                    : 'bg-blue-50 border-blue-200 text-blue-700'
                }`}>
                  <div className="flex justify-between items-center">
                    <span>Character Count:</span>
                    <span className="font-bold">
                      {charCount} / {MAX_CHARS}
                    </span>
                  </div>
                  {charCount > MAX_CHARS && (
                    <div className="mt-1 text-xs">
                      ❌ Exceeds limit by {charCount - MAX_CHARS} characters
                    </div>
                  )}
                  {charCount <= MAX_CHARS && (
                    <div className="mt-1 text-xs">
                      ✅ {getRemainingChars()} characters remaining
                    </div>
                  )}
                </div>

                {/* Message Preview at Top */}
                <div className={`bg-gradient-to-br from-${accentColor}-50 via-white to-purple-50 rounded-lg border border-${accentColor}-200 shadow-sm overflow-hidden`}>
                  <div className="p-4 border-b border-indigo-100 bg-white flex justify-between items-center">
                    <div className="flex items-center">
                      <div className={`w-1.5 h-1.5 bg-${accentColor}-500 rounded-full mr-2`}></div>
                      <span className={`text-xs font-semibold text-${accentColor}-700 uppercase tracking-wide`}>
                        {mode === 'whatsapp' ? 'Message Preview' : 'Notification Preview'}
                      </span>
                    </div>
                    {/* Platform Badge - Only show if platform is not "all" or "both" and mode is whatsapp */}
                    {mode === 'whatsapp' && shouldDisplayPlatform(platform) && (
                      <div className="text-xs px-2.5 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                        Template by {formatPlatformName(platform)}
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4 max-h-60 overflow-y-auto">
                    <div className="mb-3">
                      <p className="text-sm leading-relaxed text-gray-700 break-words whitespace-pre-wrap overflow-wrap-anywhere">
                        {renderMessageWithVariables(templatePreview.templateMessage)}
                      </p>
                    </div>

                    {/* Buttons Preview - Only for whatsapp */}
                    {mode === 'whatsapp' && hasButtons() && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex flex-wrap gap-2">
                          {/* Quick Reply Buttons */}
                          {buttons.quickReplies && buttons.quickReplies.map((button, index) => (
                            <div
                              key={button._id || index}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                                buttonErrors[button._id] 
                                  ? 'bg-red-100 text-red-800 border-red-200' 
                                  : 'bg-blue-100 text-blue-800 border-blue-200'
                              }`}
                            >
                              {getButtonDisplayText(button) || 'Empty Button'}
                            </div>
                          ))}
                          
                          {/* Action Buttons */}
                          {buttons.actions && buttons.actions.map((button, index) => (
                            <div
                              key={button._id || index}
                              className="px-3 py-1.5 bg-green-100 text-green-800 rounded-lg text-xs font-medium border border-green-200 flex items-center gap-2"
                            >
                              <span>{getButtonDisplayText(button)}</span>
                              {button.url && (
                                <button
                                  onClick={(e) => handleUrlClick(button.url, e)}
                                  className="text-green-600 hover:text-green-800 transition-colors"
                                  title={`Open: ${button.url}`}
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Buttons Preview - For notification mode */}
                    {mode === 'notification' && useButtons && (button1Text.trim() || button2Text.trim()) && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex flex-wrap gap-2">
                          {button1Text.trim() && (
                            <div
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                                button1Error
                                  ? 'bg-red-100 text-red-800 border-red-200'
                                  : 'bg-blue-100 text-blue-800 border-blue-200'
                              }`}
                            >
                              {button1Text}
                            </div>
                          )}
                          {button2Text.trim() && (
                            <div
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                                button2Error
                                  ? 'bg-red-100 text-red-800 border-red-200'
                                  : 'bg-blue-100 text-blue-800 border-blue-200'
                              }`}
                            >
                              {button2Text}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Media Section - Only for whatsapp */}
                {mode === 'whatsapp' && mediaType && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                        <span className="w-1 h-4 bg-blue-500 mr-2 rounded"></span>
                        Media Attachment
                      </h3>
                      <span className="text-xs px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                        {mediaType}
                      </span>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-md p-4">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
                        <div className="md:col-span-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Media Type
                          </label>
                          <input
                            type="text"
                            value={mediaType}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Media URL *
                          </label>
                          <input
                            type="url"
                            value={mediaUrl}
                            onChange={(e) => handleMediaUrlChange(e.target.value)}
                            placeholder="Enter media URL (image, video, document, etc.)"
                            className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all ${
                              mediaError 
                                ? 'border-red-300 bg-red-50' 
                                : 'border-gray-300 focus:border-blue-400'
                            } ${sending || messageSent ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={sending || messageSent}
                          />
                          {mediaError && (
                            <div className="mt-1 text-xs text-red-500 flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              {mediaError}
                            </div>
                          )}
                          {mediaUrl && !mediaError && (
                            <div className="mt-1 text-xs text-green-500 flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Valid URL format
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Dynamic Variables Input Section */}
                {dynamicVariables.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                        <span className={`w-1 h-4 bg-${accentColor}-500 mr-2 rounded`}></span>
                        Configure Variables
                      </h3>
                      <span className={`text-xs px-2.5 py-0.5 bg-${accentColor}-100 text-${accentColor}-700 rounded-full font-medium`}>
                        {dynamicVariables.length} variable{dynamicVariables.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      {dynamicVariables.map((variable, index) => (
                        <div key={index} className="group bg-white border border-gray-200 rounded-md p-3 hover:border-indigo-300 hover:shadow-sm transition-all">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <label className="text-sm font-semibold text-gray-700 flex items-center min-w-fit">
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-indigo-500 text-white rounded text-xs mr-2 font-bold">
                                {index + 1}
                              </span>
                              <span className="text-xs text-gray-500 font-mono">
                                {'{{'}{variable}{'}}'}
                              </span>
                            </label>
                            <div className="flex-1 min-w-0">
                              <input
                                type="text"
                                value={variableValues[variable] || ''}
                                onChange={(e) => handleVariableChange(variable, e.target.value)}
                                disabled={isVariableUsed(variable) || sending || messageSent}
                                placeholder={`Enter value for {{${variable}}} (${getRemainingChars()} chars left)`}
                                className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all break-words ${
                                  isVariableUsed(variable)
                                    ? 'bg-green-50 text-green-700 font-mono border-green-200'
                                    : 'bg-white border-gray-300 focus:border-indigo-400'
                                } ${
                                  charCount >= MAX_CHARS ? 'bg-yellow-50 border-yellow-300' : ''
                                } ${sending || messageSent ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                              />
                            </div>
                            {/* Only show "+ Name" button for WhatsApp mode */}
                            {mode === 'whatsapp' && (
                              <button
                                onClick={() => handleToggleVariable(variable)}
                                className={`text-xs px-3 py-2 rounded-md font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                                  isVariableUsed(variable)
                                    ? 'bg-red-500 text-white hover:bg-red-600 shadow-sm'
                                    : 'bg-purple-500 text-white hover:bg-purple-600 shadow-sm'
                                } ${
                                  getRemainingChars() < NAME_VAR_LENGTH && !isVariableUsed(variable) 
                                    ? 'opacity-50 cursor-not-allowed' 
                                    : ''
                                } ${sending || messageSent ? 'opacity-50 cursor-not-allowed' : ''}`}
                                disabled={getRemainingChars() < NAME_VAR_LENGTH && !isVariableUsed(variable) || sending || messageSent}
                              >
                                {isVariableUsed(variable) ? '✕ Remove' : '+ Name'}
                              </button>
                            )}
                          </div>
                          {charCount >= MAX_CHARS && !isVariableUsed(variable) && (
                            <div className="mt-1 text-xs text-red-500">
                              Character limit reached. Cannot add more text.
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notification Specific Fields - Integrated directly */}
                {mode === 'notification' && (
                  <>
                    {/* Valid Till Date */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Valid Till Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={validTillDate}
                        onChange={(e) => setValidTillDate(e.target.value)}
                        className={`w-full px-3 py-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 transition-all ${
                          validTillDate 
                            ? 'border-green-300 focus:ring-orange-400 focus:border-orange-400' 
                            : 'border-gray-300 focus:ring-orange-400 focus:border-orange-400'
                        } ${sending || messageSent ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        disabled={sending || messageSent}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      {validTillDate && (
                        <p className="text-xs text-green-500 mt-1">
                          ✓ Notification will be valid until {new Date(validTillDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    {/* Yes/No Buttons Toggle - Similar to NotificationModal */}
                    <div className="border border-gray-200 rounded-md px-3 py-2.5 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-700">Add Buttons</p>
                          <p className="text-xs text-gray-400">Worker can respond directly from the notification</p>
                        </div>
                        <button
                          type="button"
                          onClick={handleToggleUseButtons}
                          disabled={sending || messageSent}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                            useButtons ? 'bg-indigo-500' : 'bg-gray-300'
                          } ${sending || messageSent ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              useButtons ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Dynamic button value inputs - only shown when useButtons is on */}
                      {useButtons && (
                        <div className="grid grid-cols-2 gap-3 pt-1 border-t border-gray-100">
                          <div className="pt-2">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                              Button 1 Text <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={button1Text}
                              onChange={handleButton1Change}
                              onBlur={() => setButton1Error(validateButtonText(button1Text))}
                              placeholder="e.g. Yes"
                              disabled={sending || messageSent}
                              className={`w-full px-2.5 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 transition-all ${
                                button1Error
                                  ? 'border-red-300 bg-red-50 focus:ring-red-300'
                                  : 'border-gray-300 focus:ring-indigo-400 focus:border-indigo-400'
                              } ${sending || messageSent ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            <div className="flex justify-between items-center mt-1">
                              {button1Error && <p className="text-xs text-red-500">{button1Error}</p>}
                              <p className={`text-xs ml-auto ${button1Text.length > BUTTON_TEXT_MAX ? 'text-red-500' : 'text-gray-400'}`}>
                                {button1Text.length}/{BUTTON_TEXT_MAX}
                              </p>
                            </div>
                          </div>

                          <div className="pt-2">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                              Button 2 Text <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={button2Text}
                              onChange={handleButton2Change}
                              onBlur={() => setButton2Error(validateButtonText(button2Text))}
                              placeholder="e.g. No"
                              disabled={sending || messageSent}
                              className={`w-full px-2.5 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 transition-all ${
                                button2Error
                                  ? 'border-red-300 bg-red-50 focus:ring-red-300'
                                  : 'border-gray-300 focus:ring-indigo-400 focus:border-indigo-400'
                              } ${sending || messageSent ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            <div className="flex justify-between items-center mt-1">
                              {button2Error && <p className="text-xs text-red-500">{button2Error}</p>}
                              <p className={`text-xs ml-auto ${button2Text.length > BUTTON_TEXT_MAX ? 'text-red-500' : 'text-gray-400'}`}>
                                {button2Text.length}/{BUTTON_TEXT_MAX}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto mb-2 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p>Failed to load preview</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700"
            disabled={sending}
          >
            {messageSent ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canSendMessage()}
            className={`px-4 py-2 rounded-md transition-all text-sm font-medium shadow-sm ${
              messageSent 
                ? 'bg-green-500 text-white cursor-default' 
                : sending
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : `bg-${accentColor}-500 text-white hover:bg-${accentColor}-600 disabled:opacity-50 disabled:cursor-not-allowed`
            }`}
          >
            {sending ? 'Sending...' : messageSent ? successMessage : sendButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;